import { Col, DatePicker, Form, Input, Radio, Row, Select, Spin } from "antd";
import locale from "antd/es/date-picker/locale/es_ES";
import { DocumentCard, PhoneInput } from "cmt-ui-kit";
import { useTranslation } from "react-i18next";
import evaluationTypes from "../../../../utils/evaluationType.json";
import { getCookie } from "../../../../services/cookies";
import { createArea, createJobPosition } from "../../../../services/general";
import CreatableSelect from "../../../components/CreatableSelect";

const { Option } = Select;

const WorkerDataBlock = ({ workerData, form, reservation }) => {
  const countryCode = getCookie('country_code');
  const { t } = useTranslation();
  const {
    documentType,
    setDocumentType,
    handleOnBlur,
    genders,
    countries,
    areasList,
    jobPositionsList,
    projectsList,
    loadingProjects,
    isJobPositionRequired,
    showMessage,
    handleEvaluationTypeChange,
    handleBirthDateChange,
    disabledDate,
    fetchProjects,
    loadingGender,
    loadingCountries,
    handleJobPositionChange,
    handleAreaCreated,
    handleJobPositionCreated,
  } = workerData;

  return (
    <>
      <Row gutter={10} className="mb-10">
        <Col lg={24} xs={24}>
          <Form.Item
            name="evaluation_type"
            className="mb-0"
            label={<strong>Tipo de Evaluación</strong>}
            rules={[{ required: true, message: 'Tipo de evaluación es requerido' }]}
          >
            <Radio.Group className="cmt-radio-left" onChange={(e) => handleEvaluationTypeChange(e.target.value)}>
              {evaluationTypes[countryCode].map(evaluationType => (
                <Radio key={evaluationType.value} value={evaluationType.value}>{evaluationType.text}</Radio>
              ))}
            </Radio.Group>
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={15} className='mb-10'>
        <Col lg={documentType === "passport" ? 6 : 8} xs={24}>
          <Form.Item label="Tipo y número de documento" className="mb-0">
            <DocumentCard
              countryCode={countryCode}
              setDocumentType={setDocumentType}
              form={form}
              handleOnDocumentNumberBlur={handleOnBlur}
              type="person"
            />
          </Form.Item>
        </Col>
        
        {documentType === "passport" && (
          <Col lg={6} xs={24}>
            {loadingCountries ? (
              <Row justify="center"><Spin className="cmt-spin" /></Row>
            ) : (
              countries && (
                <Form.Item
                  name="countryCode"
                  label="País"
                  className="mb-0"
                  rules={[{ required: true, message: "País es requerido" }]}
                >
                  <Select
                    id="countryCode"
                    className="cmt-select"
                    placeholder="Seleccione país"
                    filterOption={(input, option) =>
                      option.children.toLowerCase().includes(input.toLowerCase())
                    }
                    showSearch
                  >
                    {countries.map((country, index) => (
                      <Option key={index} value={country.code}>{country.name}</Option>
                    ))}
                  </Select>
                </Form.Item>
              )
            )}
          </Col>
        )}

        <Col lg={documentType === "passport" ? 6 : 8} xs={24}>
          <Form.Item
            name="userFirstName"
            label="Nombres"
            className="mb-0"
            rules={[{ required: true, message: "Nombres son requeridos" }]}
          >
            <Input id="userFirstName" className="cmt-input" placeholder="Ingrese nombres" />
          </Form.Item>
        </Col>
        <Col lg={documentType === "passport" ? 6 : 8} xs={24}>
          <Form.Item
            name="userLastName"
            label="Apellidos"
            className="mb-0"
            rules={[{ required: true, message: "Apellidos son requeridos" }]}
          >
            <Input id="userLastName" className="cmt-input" placeholder="Ingrese apellidos" />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={15} className="mb-10">
        <Col lg={4} xs={24}>
          {loadingGender ? (
            <Row justify="center"><Spin className="cmt-spin" /></Row>
          ) : (
            genders && (
              <Form.Item
                name="genderId"
                label="Género"
                className="mb-0"
                rules={[{ required: true, message: "Género es requerido" }]}
              >
                <Select id="genderId" className="cmt-select" placeholder="Seleccione género">
                  {genders.map((gender, index) => (
                    <Option key={index} value={gender.id}>{gender.name}</Option>
                  ))}
                </Select>
              </Form.Item>
            )
          )}
        </Col>

        <Col lg={4} xs={24}>
          <Form.Item
            name="dateBirth"
            label="Fecha de nacimiento"
            className="mb-0"
            rules={[{ required: true, message: "Fecha de nacimiento es requerida" }]}
            extra={showMessage.datePicker && (
              <span className="underAgeAlert">
                Estás admisionando un paciente <strong>menor de edad</strong>.
              </span>
            )}
          >
            <DatePicker
              id="dateBirth"
              className="date-picker width-100"
              locale={locale}
              placeholder="Seleccione fecha de nacimiento"
              popupClassName="custom-dropdown"
              format="DD-MM-YYYY"
              disabledDate={disabledDate}
              onChange={handleBirthDateChange}
            />
          </Form.Item>
        </Col>
        
        <Col lg={2} xs={24}>
          <Form.Item name="age" label="Edad" className="mb-0">
            <Input id="age" className="cmt-input text-center" disabled />
          </Form.Item>
        </Col>

        <Col lg={6} xs={24} className="phoneCol">
          <PhoneInput.ReactPhone
            countryCode={countryCode}
            form={form}
            labelText={t("admissionPage.patientForm.phonePlaceholder")}
            optional={false}
          />
        </Col>

        <Col lg={8} xs={24}>
          <Form.Item
            name="email"
            label={t("admissionPage.patientForm.emailPlaceholder")}
            className="mb-0"
            rules={[
              { required: true, message: "Correo electrónico es requerido" },
              { type: "email", message: "Correo electrónico inválido" },
            ]}
          >
            <Input id="email" type="email" className="cmt-input" placeholder="Ingrese correo electrónico" />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={15} className='mb-10'>
        <Col lg={8} xs={24}>
          <Form.Item name="project_id" label="Proyecto" className="mb-0">
            <Select
              className="cmt-select"
              placeholder="Seleccione proyecto"
              showSearch
              allowClear
              onSearch={fetchProjects}
              filterOption={false}
              notFoundContent={loadingProjects ? <Spin size="small" /> : null}
            >
              {projectsList?.length > 0 && projectsList.map(project => (
                <Option key={project.id} value={project.id}>{project.name}</Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col lg={8} xs={24}>
          <CreatableSelect
            optionsList={areasList}
            label="Área"
            formName="area_id"
            createFn={(params) => createArea({ ...params, company_id: reservation?.company_id || form.getFieldValue('company_id') })}
            form={form}
            required={true}
            onItemCreated={(id, item) => handleAreaCreated(item)}
          />
        </Col>
        <Col lg={8} xs={24}>
          <CreatableSelect
            optionsList={jobPositionsList}
            label="Puesto"
            formName="job_position_id"
            createFn={(params) => createJobPosition({ ...params, company_id: reservation?.company_id || form.getFieldValue('company_id') })}
            form={form}
            required={isJobPositionRequired}
            handleChange={handleJobPositionChange}
            onItemCreated={(id, item) => {
              form.setFieldsValue({ profession: item?.name });
              handleJobPositionCreated(item);
            }}
          />
        </Col>
      </Row>

      <Row gutter={15}>
        <Col lg={8} xs={24}>
          <Form.Item name="external_id" label="ID Externo" className="mb-0">
            <Input id="external_id" className="cmt-input" placeholder="Ingrese ID externo" />
          </Form.Item>
        </Col>
      </Row>
    </>
  );
};

export default WorkerDataBlock;
